import { create } from 'zustand';
import { API_URL } from '../Config/config';
import axiosInstance from '../services/axiosInstance';
import { toast } from 'react-toastify';
import { parsePermissionCodes } from '../utils/permissions';

/**
 * Persist permissioncodes only inside userDataObj (never as a root key).
 * Collects codes from the login payload wherever the API sent them, then
 * stores the normalized list on userDataObj.permissioncodes.
 */
const withPermissionCodes = (data) => {
  if (!data || typeof data !== 'object') return data;

  const set = new Set();
  for (const n of parsePermissionCodes(data.userDataObj?.permissioncodes)) set.add(n);
  for (const n of parsePermissionCodes(data.userDataObj?.permission_codes)) set.add(n);
  for (const n of parsePermissionCodes(data.permissioncodes)) set.add(n);
  for (const n of parsePermissionCodes(data.permission_codes)) set.add(n);
  const permissioncodes = [...set];

  const {
    permissioncodes: _rootPermissioncodes,
    permission_codes: _rootPermissionCodes,
    ...rest
  } = data;

  return {
    ...rest,
    userDataObj: {
      ...(data.userDataObj || {}),
      permissioncodes,
    },
  };
};

const useAuthStore = create((set, get) => ({
  isAuthenticated: false,
  isLoading: false,
  user: null,

  login: async (body) => {
    set({ isLoading: true });

    const device_id = localStorage.getItem("device_id") || crypto.randomUUID();
    localStorage.setItem("device_id", device_id);

    // Extract OS details from userAgent
    const userAgent = navigator.userAgent;
    const start = userAgent.indexOf("(");
    const end = userAgent.indexOf(")");
    let device_name = "Unknown Web Device";

    if (start !== -1 && end !== -1) {
      const osDetails = userAgent.substring(start + 1, end);
      
      if (osDetails.includes("Windows NT")) {
        const osVersion = osDetails.split(";")[0];
        device_name = `${osVersion}`;
      } else if (osDetails.includes("Macintosh")) {
        device_name = "Mac";
      } else if (osDetails.includes("Linux")) {
        device_name = "Linux Machine";
      }
    }

    body.device_id = device_id;
    body.device_name = device_name;

    try {
      const result = await axiosInstance.post(API_URL + 'users/dashboard-login', body);
      const response = result.data;
      const token = response.data.token.replace(/^"|"$/g, '');

      if (response.status === 'success') {
        const userPayload = withPermissionCodes(response.data);

        // console.log(response.data, 'userPayload')

        // Store user (including permissioncodes) in localStorage
        localStorage.setItem('user', JSON.stringify(userPayload));
        localStorage.setItem('token', token);
        
        set({
          isAuthenticated: true,
          user: userPayload,
          isLoading: false,
        });

        return { status: true,message:"Login Successful", data: userPayload };
      } else {
        console.log(response, 'response from service')
        return { status: false,message:response?.message, data: [] };
      }
    } catch (err) {
      console.log(err, 'error from service')
      set({ isLoading: false });
      if(err?.status === 409) {
        return { status: 409, message: err?.response?.data.message, invalid: true };
      } 
      return { status: false, message: err?.response?.data.message, invalid: true };
    }
  },

  getOtp: async (email) => {
    try {
      const result = await axiosInstance.get(API_URL+ `users/send-otp?email=${email}`)
      const response = result.data

      return {status: true, message: response?.message, userId: response.user_id}
      
    } catch(e) {
      return {status: false, message: e?.response?.data?.message}
    }
  },

  verifyOtp: async (body) => {
    try {
      const result = await axiosInstance.post(API_URL + 'users/verify-password-otp', body)
      const response = result.data
      return {status: true, message: response?.message}
    } catch(e) {
      return {status: false, message: e?.response?.data?.message}
    }
  },

  confirmPassword: async (body) => {
   try {
    const result = await axiosInstance.post(API_URL + 'users/reset-password', body)
    const response = result.data
    return {status: true, message: response?.message}
   } catch(e) {
    console.log(e)
    return {status: false, message: e?.response?.data?.message}
   }
  },

  logout: async () => {
    const user = JSON.parse(localStorage.getItem('user'))
    const user_id = user?.userDataObj?.user_id
    try {
      const result = await axiosInstance.post(API_URL + 'users/logout', {
        user_id: user_id
      })
      const response = result?.data
      if(response?.status === "success") {
        toast.success(response?.message, {toastId: 'successMessageLogout'})
        set({
          isAuthenticated: false,
          user: null,
        });
        localStorage.clear();
      }
    } catch(e) {
      console.log(e)
    }
    
  },

  checkIsLoggedin: () => {
    const userData = localStorage.getItem('user');

    if (userData) {
      // Parse stored user and re-normalize permissioncodes for older sessions
      if (!get().isAuthenticated) {
        const parsed = withPermissionCodes(JSON.parse(userData));
        localStorage.setItem('user', JSON.stringify(parsed));
        set({
          isAuthenticated: true,
          user: parsed,
        });
      }
    } else {
      // Call logout if user is not logged in
      if (get().isAuthenticated) {
        get().logout();
      }
    }
  },
}));

export { useAuthStore };